import React from 'react';
import { motion } from 'framer-motion';
import { FaTooth, FaUsers, FaHeart, FaAward } from 'react-icons/fa';
import PageMeta from './PageMeta';

const teamMembers = [
  {
    name: 'Dr. Sarah Johnson',
    role: 'Lead Dentist',
    description: 'With over 15 years of experience, Dr. Johnson specializes in cosmetic and restorative dentistry.',
    image: 'https://via.placeholder.com/150', // Placeholder image
  },
  {
    name: 'Dr. Michael Chen',
    role: 'Orthodontist',
    description: 'Dr. Chen is passionate about creating beautiful smiles through orthodontic treatments.',
    image: 'https://via.placeholder.com/150', // Placeholder image
  },
  {
    name: 'Dr. Emily Davis',
    role: 'Pediatric Dentist',
    description: 'Dr. Davis makes dental visits fun and comfortable for children of all ages.',
    image: 'https://via.placeholder.com/150', // Placeholder image
  },
];

const AboutUs = () => {
  return (
    <div className="bg-phoenix-beige min-h-screen">
      <PageMeta
        title="About Us | Phoenix Dental"
        description="Learn more about Phoenix Dental, our mission, and our experienced team of dental professionals."
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-phoenix-beige via-phoenix-white to-phoenix-beige py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            className="text-5xl lg:text-6xl font-bold text-phoenix-black mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            About <span className="text-phoenix-gold">Phoenix Dental</span>
          </motion.h1>
          <motion.p
            className="text-xl text-phoenix-gray max-w-3xl mx-auto mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Dedicated to providing exceptional dental care with compassion, expertise, and the latest technology.
            Your smile is our passion, and your health is our priority.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <FaTooth className="text-6xl text-phoenix-gold mx-auto" />
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-phoenix-gold mb-6">Our Mission</h2>
            <p className="text-lg text-phoenix-gray max-w-4xl mx-auto">
              At Phoenix Dental, our mission is to transform lives through healthy, beautiful smiles. We believe that
              everyone deserves access to high-quality dental care in a comfortable and welcoming environment. Our
              team is committed to staying at the forefront of dental technology and techniques to provide the best
              possible outcomes for our patients.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <FaUsers className="text-5xl text-phoenix-gold mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-phoenix-black mb-2">Experienced Team</h3>
              <p className="text-phoenix-gray">Our skilled professionals bring years of expertise to every treatment.</p>
            </motion.div>
            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <FaHeart className="text-5xl text-phoenix-gold mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-phoenix-black mb-2">Patient Care</h3>
              <p className="text-phoenix-gray">We prioritize your comfort and well-being in every interaction.</p>
            </motion.div>
            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <FaAward className="text-5xl text-phoenix-gold mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-phoenix-black mb-2">Quality Service</h3>
              <p className="text-phoenix-gray">We use the latest technology and techniques for optimal results.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="bg-phoenix-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            className="text-3xl font-bold text-phoenix-gold mb-12 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            Meet Our Team
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {teamMembers.map(({ name, role, description, image }, index) => (
              <motion.div
                key={index}
                className="bg-phoenix-beige rounded-lg p-6 shadow-md text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
              >
                <img src={image} alt={name} className="w-24 h-24 rounded-full mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-phoenix-black mb-1">{name}</h3>
                <p className="text-phoenix-gold font-medium mb-3">{role}</p>
                <p className="text-phoenix-gray">{description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
